import React, { useEffect, useState } from 'react';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import PropTypes from 'prop-types';
import AlertModal from 'components/common-components/AlertModal';
import TextField from 'components/form-components/TextField';
import MobileNumberField from 'components/form-components/MobileNumberField';
import FormButton from 'components/form-components/FormButton';
import toast from 'react-hot-toast';
import { CallAPI } from 'actions/General';
import { useTranslation } from 'react-i18next';

const defaultRow = {
  firstName: '',
  lastName: '',
  countryCode: '+966',
  mobileNumber: '',
  email: '',
};

const GuestUserEditModal = ({ editMode, data, handleClose, refreshList }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState([defaultRow]);
  const [errors, setErrors] = useState([{}]);
  const [touched, setTouched] = useState([{}]);

  const handleChange = (field, value) => {
    const [key, index] = field.split('_');

    const updatedValues = [...values];
    updatedValues[index] = { ...updatedValues[index], [key]: value };
    setValues(updatedValues);

    const updatedTouched = [...touched];
    updatedTouched[index] = { ...updatedTouched[index], [key]: true };
    setTouched(updatedTouched);

    const updatedErrors = [...errors];
    updatedErrors[index] = { ...updatedErrors[index], [key]: undefined };
    setErrors(updatedErrors);
  };

  const validateRows = (values) => {
    const schemaErrors = values.map(() => ({}));
    let hasError = false;

    values.forEach((row, index) => {
      // First Name & Last Name required
      if (!row.firstName.trim()) {
        schemaErrors[index].firstName = 'First name is required';
        hasError = true;
      }
      if (!row.lastName.trim()) {
        schemaErrors[index].lastName = 'Last name is required';
        hasError = true;
      }

      // Email or MobileNumber required
      if (!row.email.trim() && !row.mobileNumber.trim()) {
        const msg = 'Email or phone number is required';
        schemaErrors[index].email = msg;
        schemaErrors[index].mobileNumber = msg;
        hasError = true;
      } else if (row.email.trim()) {
        // validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(row.email.trim())) {
          schemaErrors[index].email = 'Invalid email';
          hasError = true;
        }
      }
    });

    return { hasError, schemaErrors };
  };

  const handleSubmit = async () => {
    setTouched([{}]);

    const { hasError, schemaErrors } = validateRows(values);
    if (hasError) {
      setErrors(schemaErrors);
      return;
    }

    const payload = {
      users: values.map((v) => ({
        ...(editMode ? { id: v.id } : {}),
        firstName: v.firstName,
        lastName: v.lastName,
        countryCode: v.mobileNumber ? v.countryCode : null,
        mobileNumber: v.mobileNumber || null,
        email: v.email || null,
      })),
    };

    const result = await CallAPI(
      editMode ? 'UPDATE_BULK_DIRECTORY_USERS' : 'ADD_BULK_DIRECTORY_USERS',
      payload,
    );
    if (result.status) {
      toast.success(editMode ? "User's updated successfully" : "User's added successfully");
      refreshList();
      handleClose();
    } else {
      toast.error(result.error);
    }
  };

  useEffect(() => {
    if (!editMode) return;

    const formattedValues = data.map((item) => ({
      id: item.id,
      firstName: item.firstName || defaultRow.firstName,
      lastName: item.lastName || defaultRow.lastName,
      countryCode: item.countryCode || defaultRow.countryCode,
      mobileNumber: item.mobileNumber || defaultRow.mobileNumber,
      email: item.email || defaultRow.email,
    }));

    setValues(formattedValues);
  }, [data]);

  const addRow = () => {
    setValues([...values, { ...defaultRow }]);
  };

  const removeRow = (index) => {
    if (values.length === 1) return;

    const updated = [...values];
    updated.splice(index, 1);
    setValues(updated);
  };

  return (
    <AlertModal show={true} actionButtons={[]} noPadding size='xl'>
      <div className='w-100'>
        {/* Header */}
        <div className='p-3 d-flex justify-content-between border-bottom-grey-10-h-1'>
          <span className='theme-font-inter-semi-bold theme-size-1_2 theme-text-black-1'>
            {t(editMode ? 'text_update_users_to_directory' : 'text_add_users_to_directory')}
          </span>
          <img className='p-2 cursor-pointer' src={crossGreyIcon} onClick={handleClose} />
        </div>

        <div className='px-3 mt-3 mb-1 d-flex align-items-center gap-3'>
          <div className='flex-grow-1 d-flex gap-3'>
            <div className='w-100 theme-text-black-1'>{t('table_header_first_name')}</div>
            <div className='w-100 theme-text-black-1'>{t('table_header_last_name')}</div>
            <div className='w-100 theme-text-black-1'>{t('table_header_phone_number')}</div>
            <div className='w-100 theme-text-black-1'>{t('table_header_email')}</div>
          </div>
          <div className='d-flex justify-content-center px-2 invisible'>
            <img src={crossGreyIcon} />
          </div>
        </div>

        <div className='px-3' style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {/* Dynamic Rows */}
          <div className='d-flex flex-column gap-3'>
            {values.map((row, index) => (
              <div key={index} className='d-flex align-items-center gap-3'>
                <div className='flex-grow-1 d-flex gap-3'>
                  <div className='w-100'>
                    <TextField
                      name={`firstName_${index}`}
                      placeholder='First Name'
                      value={row.firstName}
                      handleChange={handleChange}
                      error={errors[index]?.firstName}
                      touched={touched[index]?.firstName}
                      shrink
                    />
                  </div>

                  <div className='w-100'>
                    <TextField
                      name={`lastName_${index}`}
                      placeholder='Last Name'
                      value={row.lastName}
                      handleChange={handleChange}
                      error={errors[index]?.lastName}
                      touched={touched[index]?.lastName}
                      shrink
                    />
                  </div>

                  <div className='w-100'>
                    <MobileNumberField
                      fieldProps={{ name: `mobileNumber_${index}`, placeholder: '123123123' }}
                      codeName={`countryCode_${index}`}
                      value={{
                        countryCode: row.countryCode,
                        mobileNumber: row.mobileNumber,
                      }}
                      error={errors[index]?.mobileNumber}
                      touched={touched[index]?.mobileNumber}
                      handleChange={handleChange}
                      shrink
                    />
                  </div>

                  <div className='w-100'>
                    <TextField
                      name={`email_${index}`}
                      placeholder='Email'
                      value={row.email}
                      handleChange={handleChange}
                      error={errors[index]?.email}
                      touched={touched[index]?.email}
                      shrink
                    />
                  </div>
                </div>

                {/* Remove Row */}
                <div
                  className={`d-flex justify-content-center px-2 py-3 ${
                    values.length === 1 ? 'opacity-50 cursor-none pe-none' : 'cursor-pointer'
                  }`}
                  onClick={() => removeRow(index)}
                >
                  <img src={crossGreyIcon} />
                </div>
              </div>
            ))}
          </div>

          {/* Add Button */}
          {!editMode && (
            <div className='mt-4 d-flex gap-2 py-2'>
              <img src={plusIcon} />
              <label className='theme-text-green-1 cursor-pointer' onClick={addRow}>
                {t('text_add_another_user')}
              </label>
            </div>
          )}
        </div>

        <div className='p-3 mt-5 d-flex justify-content-end border-top-grey-10-h-1'>
          <div className='d-flex gap-3'>
            <FormButton text='button_cancel' variant='green-2' onClick={handleClose} />
            <FormButton
              text={editMode ? 'button_update' : 'button_add'}
              variant='green-1'
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </AlertModal>
  );
};

GuestUserEditModal.propTypes = {
  editMode: PropTypes.bool,
  data: PropTypes.array,
  handleClose: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
};

export default GuestUserEditModal;
